import React, { useState, useEffect, useRef } from "react";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import { Paragraph } from "@arctheme-components/elements/Paragraph/Paragraph";
import { Headings } from "@arctheme-components/elements/Headings/Headings";
import { BlogArticleTile } from "@project-stories/blog/BlogArticleTile/BlogArticleTile";
import {Button} from "@mindarc/arctheme-components/frontend/src/stories/components/elements/Button/Button";
import useColoredTextParser from "@project-stories/../hooks/useColoredTextParser"; 

const CarouselLayout = ({ initialArticles, settings }) => {
  const isMobile = useMobileBreakpoint(settings.mobile_breakpoint);
  const { globals: { shop_url } } = settings;
  const [currentPage, setCurrentPage] = useState(1);
  const [articlesPerPage] = useState(20);
  const [articles, setArticles] = useState(initialArticles || []);
  const [currentArticles, setCurrentArticles] = useState(
    initialArticles?.slice(0, articlesPerPage) || [],
  );

  const carouselRef = useRef(null);

  const { section, blocks } = settings;
  let flexDirection = "flex-row";
  let navArrowsPosition = isMobile
    ? section.nav_arrows_position_mobile
    : section.nav_arrows_position;

  switch (navArrowsPosition) {
    case "bottom":
      flexDirection = "flex-col";
      break;
  }


  const taglineText =  section.tagline;
  const taglineType = isMobile
    ? section.tagline_type_mobile || section.tagline_type
    : section.tagline_type_desktop || section.tagline_type;
  const titleText = section.title;
  const titleType = isMobile
    ? section.title_type_mobile || section.title_type
    : section.title_type_desktop || section.title_type;
  const showMetadata = section.show_metadata !== false;
  const showCTA = isMobile ?  false : section.show_read_more;

  const renderFormattedTagline = useColoredTextParser({
    title: taglineText,
    accentTitleParts: section?.accent_title_parts,
    accentColor: section?.accent_color,
    headerStyle: taglineType,
    seoStyle: section?.seo_title_font_type,
    HeadingsComponent: Headings,
  });

  const renderFormattedHeading = useColoredTextParser({
    title: titleText,
    accentTitleParts: section?.accent_title_parts,
    accentColor: section?.accent_color,
    headerStyle: titleType,
    seoStyle: section?.seo_title_font_type,
    HeadingsComponent: Headings,
  });
  

  const getArticleUrl = (currentArticle) => {
      let articleUrl = `/blogs/${currentArticle.handle}`;
      if (currentArticle.blog?.handle && !currentArticle.handle.includes(currentArticle.blog.handle)) {
          articleUrl = `/blogs/${currentArticle.blog.handle}/${currentArticle.handle}`;
      } else if (settings?.blog?.handle && !currentArticle.handle.includes(settings.blog.handle)) {
          articleUrl = `/blogs/${settings.blog.handle}/${currentArticle.handle}`;
      }
      return articleUrl;
  };



  useEffect(() => {
    const handleArticlesComplete = (event) => {
      setCurrentPage(1);
      setArticles(event.detail.articles);
    };

    const handleNextPage = (event) => {
      if (event.detail.type === "pagination") {
        setCurrentPage((prevPage) => prevPage + 1);
      }
    };

    window.addEventListener("blog-articles-complete", handleArticlesComplete);
    window.addEventListener("nextpage", handleNextPage);

    return () => {
      window.removeEventListener(
        "blog-articles-complete",
        handleArticlesComplete,
      );
      window.removeEventListener("nextpage", handleNextPage);
    };
  }, []);

  useEffect(() => {
    const indexOfLastArticle = currentPage * articlesPerPage;
    const newArticles = articles.slice(0, indexOfLastArticle);
    setCurrentArticles(newArticles);
  }, [currentPage, articles, articlesPerPage]);

  const contentBlockRender = () => (
    <div className="flex flex-col text-center">
      <div> 
      {taglineText && (
         renderFormattedTagline()

      )}
      </div>
      <div className="mt-3 mb-4"> 
        {titleText && (
        renderFormattedHeading()
        )}
      </div>

      
      {section?.cta_text && section?.cta_url &&  (
              <Button
                style={section?.cta_style}
                label={section?.cta_text}
                link={section?.cta_url}

                onClick={undefined}
              />
      )}
      

    </div>
  );

  const {
    is_bleed_carousel,
    padding_left_right_desktop,
    is_bleed_carousel_mobile,
    padding_left_right_mobile,
  } = settings.section;

  const paddingDesktop = is_bleed_carousel
    ? `lg:pr-[${padding_left_right_desktop}px]`
    : '';

  const paddingMobile = is_bleed_carousel_mobile
    ? `pr-[${padding_left_right_mobile}px]`
    : '';

  return (
    <div
      className={`blog-articles-carousel w-full color-${section.color_schema}`}
    >
      <div
        className={`section-toprow flex flex-col items-center justify-between ${paddingDesktop} ${paddingMobile}`}
      >
        <div className={"section-toprow__details flex mb-[64px]"}>
          {contentBlockRender()}
        </div>
      </div>

      <div
        className={`blog-articles carousel-item-container flex ${flexDirection} w-full space-[24px]`}
      >

        {currentArticles.length > 0 && (
          <div
            {...{
              "x-data": "extended_carousel",
              "x-init": `$nextTick(() => { initialise(true, false, false, false, false, 'test') })`,
              "x-ref": "carousel",
            }}
            className={`carousel col-start-1 row-start-1 article-items gap-x-[20px]`}
            ref={carouselRef}
          >
            {currentArticles.map((article, index) => (
              <div
                className="carousel-item blog-articles__article article group"
                key={index}
              >
                <BlogArticleTile
                  article={article}
                  show_excerpt={true}
                  settings={settings}
                  showContent={false}
                  showCategory={true}
                  showMetadata={showMetadata}
                />
                { showCTA  &&
                <div className={"mx-auto pt-6 justify-center flex"}>
                  <Button
                    style={'secondary'}
                    label={'Read More'}
                    link={`${shop_url}${getArticleUrl(article)}`}
                    className={"w-[200px] opacity-0 group-hover:opacity-100 transition-opacity duration-200"}
                    onClick={undefined}
                  />
                </div>  }
              </div>
            ))}
          </div>
        )}
        {currentArticles.length <= 0 && (
          <div className={"blog-articles-empty"}>
            <Paragraph text={"There are no articles."} type={"b2"} />
          </div>
        )}
      </div>


        
    </div>
  );
};

export default CarouselLayout;
